import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X, ZoomIn, Download } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface GalleryImage {
  url: string;
  caption?: string;
}

interface GalleryCarouselProps {
  images: GalleryImage[];
  className?: string;
}

export function GalleryCarousel({ images, className }: GalleryCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!images || images.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No images in this gallery
      </div>
    );
  }

  const isFirstImage = currentIndex === 0;
  const isLastImage = currentIndex === images.length - 1;

  const handlePrevious = () => {
    if (!isFirstImage) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (!isLastImage) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      handlePrevious();
    } else if (e.key === "ArrowRight") {
      handleNext();
    } else if (e.key === "Escape") {
      setIsFullscreen(false);
    }
  };

  const currentImage = images[currentIndex];

  return (
    <div className={cn("space-y-4", className)} tabIndex={0} onKeyDown={handleKeyDown}>
      {/* Main Image Display */}
      <div className="relative group">
        <div className="aspect-video bg-muted rounded-lg overflow-hidden flex items-center justify-center">
          <img
            src={currentImage.url}
            alt={currentImage.caption || `Gallery image ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain cursor-pointer transition-transform hover:scale-[1.02]"
            onClick={() => setIsFullscreen(true)}
          />
        </div>

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <Button
              variant="secondary"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg disabled:opacity-30"
              onClick={handlePrevious}
              disabled={isFirstImage}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg disabled:opacity-30"
              onClick={handleNext}
              disabled={isLastImage}
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </>
        )}

        {/* Zoom Button */}
        <Button
          variant="secondary"
          size="icon"
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
          onClick={() => setIsFullscreen(true)}
        >
          <ZoomIn className="h-4 w-4" />
        </Button>
      </div>

      {/* Caption */}
      {currentImage.caption && (
        <p className="text-sm text-muted-foreground text-center italic">
          {currentImage.caption}
        </p>
      )}

      {/* Thumbnail Navigation */}
      {images.length > 1 && (
        <div className="flex items-center justify-center gap-2">
          {/* Dots for small galleries */}
          {images.length <= 10 && (
            <div className="flex items-center gap-1.5">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={cn(
                    "w-2.5 h-2.5 rounded-full transition-all",
                    idx === currentIndex
                      ? "bg-primary scale-125"
                      : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                  )}
                  aria-label={`Go to image ${idx + 1}`}
                />
              ))}
            </div>
          )}
          
          {/* Counter */}
          <span className="text-sm text-muted-foreground ml-2">
            {currentIndex + 1} / {images.length}
          </span>
        </div>
      )}

      {/* Thumbnail Strip for larger galleries */}
      {images.length > 4 && images.length <= 20 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2 px-1">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={cn(
                "flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all",
                idx === currentIndex
                  ? "border-primary ring-2 ring-primary/30"
                  : "border-transparent opacity-70 hover:opacity-100"
              )}
            >
              <img
                src={img.url}
                alt={img.caption || `Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Fullscreen Dialog */}
      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
          <div
            className="relative w-full h-[90vh] flex items-center justify-center"
            tabIndex={0}
            onKeyDown={handleKeyDown}
          >
            <img
              src={currentImage.url}
              alt={currentImage.caption || `Gallery image ${currentIndex + 1}`}
              className="max-w-full max-h-full object-contain"
            />

            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white hover:bg-white/20"
              onClick={() => setIsFullscreen(false)}
            >
              <X className="h-6 w-6" />
            </Button>

            {/* Download Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-16 text-white hover:bg-white/20"
              asChild
            >
              <a href={currentImage.url} download target="_blank" rel="noopener noreferrer">
                <Download className="h-5 w-5" />
              </a>
            </Button>

            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12 disabled:opacity-30"
                  onClick={handlePrevious}
                  disabled={isFirstImage}
                >
                  <ChevronLeft className="h-8 w-8" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12 disabled:opacity-30"
                  onClick={handleNext}
                  disabled={isLastImage}
                >
                  <ChevronRight className="h-8 w-8" />
                </Button>
              </>
            )}

            {/* Caption and Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center text-white">
              {currentImage.caption && (
                <p className="text-sm mb-2 max-w-md">{currentImage.caption}</p>
              )}
              <span className="text-sm opacity-70">
                {currentIndex + 1} / {images.length}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
